import {
  Box,
  LinearProgress,
  Stack,
  styled,
  Typography
} from '@mui/material';
import _ from 'lodash';
import {
  appFormUtil,
  uploadUtil
} from 'modules/common';
import React, {
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react';


const ContainerDiv = styled('div')({
  flex: '1 0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch'
});

const HiddenDiv = styled('div')(() => ({
  display: 'none'
}));

export type StyledMultiPartUploadFieldProps<T extends object> = {
  children?: ReactNode;
  isMultiUpload?: boolean;
  form: appFormUtil.InferReturn<T>;
  fieldName: keyof T;
  isUploadingFieldName: keyof T;
  uploadCountFieldName: keyof T;
};

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
export const FC = <T extends object>(props: StyledMultiPartUploadFieldProps<T>) => {
  const refInput = useRef<HTMLInputElement>(null);

  const [
    files,
    setFiles
  ] = useState<Record<string, number>>({
  });



  useEffect(
    () => {
      const sub = uploadUtil.getProgressObservable().subscribe((progress) => {
        setFiles((prev) => ({
          ...prev,
          [progress.fileName]: progress.progress
        }));

        if (!props.isMultiUpload && progress.uploadId) {
          props.form.dispatch({
            type: 'BATCH_UPDATE',
            payload: {
              ...props.form.formState,
              [props.fieldName]: progress.uploadId
            }
          });
        } else if (props.isMultiUpload && progress.uploadId) {
          const existing = props.form.formState[props.fieldName];
          if (_.isArray(existing)) {
            const newValue = [
              ...existing,
              progress.uploadId
            ];
            props.form.dispatch({
              type: 'BATCH_UPDATE',
              payload: {
                ...props.form.formState,
                [props.fieldName]: newValue
              }
            });
          }
        }

      });
      return () => sub.unsubscribe();
    },
    [
      props.fieldName,
      props.form,
      props.isMultiUpload,
      props.isUploadingFieldName
    ]
  );

  useEffect(
    () => {
      const sub = uploadUtil.getQueueUploadObservable().subscribe((progress) => {
        if (progress.message === 0) {
          props.form.dispatch({
            type: 'UPDATE',
            payload: {
              property: props.isUploadingFieldName,
              value: false as T[keyof T]
            }
          });
        } else if (progress.message > 0 && props.form.formState[props.isUploadingFieldName] !== true) {

          props.form.dispatch({
            type: 'UPDATE',
            payload: {
              property: props.isUploadingFieldName,
              value: true as T[keyof T]
            }
          });
        }
      });
      return () => sub.unsubscribe();
    }, [
      props.form,
      props.isUploadingFieldName
    ]
  );


  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    const numOfUploads = e.dataTransfer.files.length;

    if (props.isUploadingFieldName) {
      props.form.dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...props.form.formState,
          [props.isUploadingFieldName]: true as T[keyof T],
          [props.uploadCountFieldName]: props.form.formState[props.uploadCountFieldName] as number + numOfUploads as T[keyof T]
        }
      });
    }
    uploadUtil.addToQueue(e.dataTransfer.files, 'ApiFile');
  };


  return (
    <Box>
      <ContainerDiv
        onDrop={(e) => handleDrop(e)}
        onDragOver={
          (event) => event.preventDefault()
        }
        onDragLeave={
          (event) => event.preventDefault()
        }
        onClick={() => (refInput.current?.click())}
      >
        <HiddenDiv>
          <input
            ref={refInput}
            type='file'
            onChange={(e) => {
              e.preventDefault();
              const files = e.currentTarget.files ?? null;
              if (!files) {
                return;
              }
              if (files.length > 0) {
                const numOfUploads = files.length;

                if (props.isUploadingFieldName) {
                  props.form.dispatch({
                    type: 'BATCH_UPDATE',
                    payload: {
                      ...props.form.formState,
                      [props.isUploadingFieldName]: true as T[keyof T],
                      [props.uploadCountFieldName]: (props.form.formState['uploadCount' as keyof T] as number) + numOfUploads as T[keyof T]
                    }
                  });
                }
              }
              uploadUtil.addToQueue(files, 'ApiFile');
            }}

          />
        </HiddenDiv>
        {props.children ?? (

          <Box
            height='10rem'
            width='30rem'
            borderRadius='1rem'
            bgcolor={(theme) => theme.palette.grey[300]}
            alignContent='center'
            textAlign='center'
          >
            <Typography
              textAlign='center'
              variant='button'

            >
              Drop Files Here to Upload
            </Typography>
          </Box>
        )}
      </ContainerDiv>
      <Stack
        marginTop='1rem'
        direction='column'
      >
        {_.entries(files).map((node, i) => {
          return (
            <Box
              key={i}
            >
              <Typography

                variant='formLabel'
              >
                {node[0]}
              </Typography>
              <LinearProgress
                variant='determinate'
                valueBuffer={node[1] ?? 0}
                value={node[1] ?? 0}
              />
            </Box>
          );
        })}
      </Stack>
    </Box>


  );
};
